import React, { FC, useCallback } from "react";

import { faColumns } from "@fortawesome/pro-solid-svg-icons/faColumns";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Tooltip from "antd/es/tooltip";
import { useIntl } from "react-intl";

import { EntityTypesSupportedColumnSelection } from "@mapmycustomers/shared/types/entity";
import IField from "@mapmycustomers/shared/types/fieldModel/IField";
import ColumnModel from "@mapmycustomers/shared/types/viewModel/internalModel/ColumnModel";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import useChangeTracking from "@mapmycustomers/shared/util/hook/useChangeTracking";
import { Button } from "@mapmycustomers/ui";

import SelectColumnsPane from "@app/scene/dashboard/components/cards/common/DrillDownModal/components/SelectColumnsPane";
import useDrillDownView from "@app/scene/dashboard/components/cards/common/DrillDownModal/util/useDrillDownView";
import useAnalytics from "@app/util/contexts/useAnalytics";

export interface SelectColumnsButtonProps {
  fieldFilter?: (field: IField) => boolean;
}

const SelectColumnsButton: FC<SelectColumnsButtonProps> = ({ fieldFilter }) => {
  const intl = useIntl();

  const { entityType, updateViewState, viewState } = useDrillDownView();

  const analytics = useAnalytics();
  const [selectColumnsPaneVisible, showSelectColumns, hideSelectColumns] = useBoolean();

  useChangeTracking(() => {
    if (selectColumnsPaneVisible) {
      analytics.clicked(["Show/Hide Columns"]);
    }
  }, [analytics, selectColumnsPaneVisible]);

  const handleColumnsChange = useCallback(
    (columns: ColumnModel) => updateViewState({ columns }),
    [updateViewState]
  );

  return (
    <>
      <Tooltip
        placement="bottom"
        title={intl.formatMessage({
          id: "dashboard.drillDown.action.showHideColumns",
          defaultMessage: "Hide/Show columns",
          description: "Show column selector option in the DrillDown header on the Dashboard page",
        })}
      >
        <Button
          icon={<FontAwesomeIcon icon={faColumns} />}
          iconOnly
          onClick={showSelectColumns}
          type="text"
        />
      </Tooltip>
      {selectColumnsPaneVisible && (
        <SelectColumnsPane
          columns={viewState.columns}
          entityType={entityType as EntityTypesSupportedColumnSelection}
          fieldFilter={fieldFilter}
          onHide={hideSelectColumns}
          onUpdateColumns={handleColumnsChange}
        />
      )}
    </>
  );
};

export default SelectColumnsButton;
